import { Box, Button, CircularProgress, Paper, Stack, Typography, styled } from '@mui/material';

import { type ConnectStatementsInstitution } from '../hooks/useConnectStatementsData';

import Tag from 'modules/common/components/MUI/Tag';
import CheckCircleIcon from 'modules/common/components/Icons/CheckCircleIcon';
import LightningBoltIcon from 'modules/common/components/Icons/LightningBoltIcon';
import InformationCircleIcon from 'modules/common/components/Icons/InformationCircleIcon';
import AccountLogo from 'modules/HubV2/Bookkeeping/components/Banks/BankAccount/AccountLogo';
import useQuilttConnect from 'modules/common/components/StatementUpload/hooks/useQuilttConnect';

// Visual styling lives in styled() (not `sx`) per repo rules — `sx` is layout-only here.
const CardPaper = styled(Paper)(({ theme }) => ({
    borderRadius: theme.spacing(1.5),
    borderColor: theme.palette.grey[100],
}));

const CardHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
}));

const EnableBanner = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.purple[50],
}));

const EnableIconCircle = styled(Box)(({ theme }) => ({
    width: theme.spacing(5),
    height: theme.spacing(5),
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
}));

const EnableIcon = styled(LightningBoltIcon)(({ theme }) => ({
    color: theme.palette.common.white,
    fontSize: theme.spacing(2.5),
}));

const InfoIcon = styled(InformationCircleIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.spacing(2),
}));

const NotSupportedNotice = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 3),
    backgroundColor: theme.palette.cement[50],
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
}));

const AccountRow = styled(Stack, { shouldForwardProp: (prop) => prop !== 'isLast' })<{ isLast?: boolean }>(
    ({ theme, isLast }) => ({
        padding: theme.spacing(2, 3),
        borderBottom: isLast ? 0 : `1px solid ${theme.palette.grey[100]}`,
    })
);

/** Friendly label for the canonical account type returned by the onboarding progress payload. */
export function accountTypeLabel(type: string | null | undefined): string {
    const normalized = type?.toUpperCase();
    if (normalized === 'CREDIT') {
        return 'Credit card';
    }
    if (normalized === 'DEPOSITORY') {
        return 'Bank account';
    }
    return type ?? '';
}

/** The institution-level support state, applied uniformly to every account in the card. */
type InstitutionStatementStatus = 'enabled' | 'supported' | 'not_supported' | 'checking';

function StatementStatusBadge({ status }: Readonly<{ status: InstitutionStatementStatus }>) {
    if (status === 'enabled') {
        return <Tag color="success" variant="filled" icon={<CheckCircleIcon />} label="ENABLED" />;
    }
    if (status === 'supported') {
        return <Tag color="success" label="SUPPORTED" />;
    }
    if (status === 'not_supported') {
        return <Tag color="secondary" label="NOT SUPPORTED" />;
    }
    // 'checking' — resolvability not yet resolved; render nothing to avoid flashing a wrong badge.
    return null;
}

interface InstitutionStatementCardProps {
    readonly institution: ConnectStatementsInstitution;
    /** Institution-level Quiltt resolvability: true/false once checked, null while checking. */
    readonly isResolvable: boolean | null;
    /** Whether the member enabled Auto Statements for this institution in this session. */
    readonly isEnabled: boolean;
    /** Universal Quiltt connector id (backend-provided). */
    readonly connectorId: string | undefined;
    readonly onEnabled: (institutionId: string, connectionId?: string) => void;
}

export default function InstitutionStatementCard({
    institution,
    isResolvable,
    isEnabled,
    connectorId,
    onEnabled,
}: Readonly<InstitutionStatementCardProps>) {
    const { open: openQuilttConnector, isConnecting } = useQuilttConnect({
        connectorId: connectorId ?? '',
        institutionName: institution.institutionName,
        sourceSurface: 'onboarding_accounting_setup',
        onSuccess: (metadata) => onEnabled(institution.institutionId, metadata?.connectionId),
    });

    // isEnabled wins first: a connected institution must never regress to NOT SUPPORTED if a check flaps.
    const status: InstitutionStatementStatus = (() => {
        if (isEnabled) {
            return 'enabled';
        }
        if (isResolvable === null) {
            return 'checking';
        }
        if (!isResolvable) {
            return 'not_supported';
        }
        return 'supported';
    })();

    // Show the Enable banner only for a resolvable institution that has a connector and isn't enabled yet.
    const showEnableBanner = status === 'supported' && Boolean(connectorId);

    return (
        <CardPaper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
            <Stack direction="column">
                <CardHeader>
                    <Typography variant="subtitle1">{institution.institutionName}</Typography>
                </CardHeader>

                {showEnableBanner && (
                    <EnableBanner direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
                            <EnableIconCircle
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                <EnableIcon />
                            </EnableIconCircle>
                            <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography variant="subtitle2">
                                        Enable Auto Statements for {institution.institutionName}
                                    </Typography>
                                    <InfoIcon />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Skip the monthly uploads. Quick and secure setup.
                                </Typography>
                            </Box>
                        </Stack>
                        <Button
                            variant="contained"
                            color="onyx"
                            onClick={openQuilttConnector}
                            disabled={isConnecting}
                            sx={{ flexShrink: 0 }}>
                            {isConnecting ? <CircularProgress size={20} color="inherit" /> : 'Enable'}
                        </Button>
                    </EnableBanner>
                )}

                {status === 'not_supported' && (
                    <NotSupportedNotice>
                        <Typography variant="body2" color="text.secondary">
                            Auto Statements not supported. Manual statement uploads will be required.
                        </Typography>
                    </NotSupportedNotice>
                )}

                <Box>
                    {institution.accounts.map((account, index) => (
                        <AccountRow
                            key={account.id}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                            isLast={index === institution.accounts.length - 1}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
                                <AccountLogo institutionId={institution.institutionId} width={40} height={40} />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="subtitle2" noWrap>
                                        {account.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {accountTypeLabel(account.type)} • {account.mask}
                                    </Typography>
                                </Box>
                            </Stack>
                            <StatementStatusBadge status={status} />
                        </AccountRow>
                    ))}
                </Box>
            </Stack>
        </CardPaper>
    );
}
